import { useState, useEffect, useRef } from "react";
import { Settings2 } from "lucide-react";
import { cn } from "@/lib/cn";

export interface ColumnDef {
  key: string;
  label: string;
  defaultVisible?: boolean; // defaults to true
}

interface Props {
  /** Unique storage key for localStorage persistence */
  storageKey: string;
  /** All available columns */
  columns: ColumnDef[];
  /** Called whenever visibility changes */
  onChange: (visibleKeys: Set<string>) => void;
}

/** Persisted picker state.
 *
 *   visible — keys the user wants visible (their explicit choice)
 *   known   — every key the table KNEW about at the time of save. Used
 *             to distinguish "the user toggled this off" from "this
 *             column was added after the user last saved." Hidden
 *             columns belong to the first category; new columns belong
 *             to the second and merge in as visible.
 *
 * Legacy callers may have written a bare string[] to localStorage. Treat
 * those as { visible: arr, known: arr } so old saves migrate cleanly:
 * the user's previously-visible set is kept; nothing they had hidden
 * resurfaces. New columns added after the legacy save still merge in.
 */
interface SavedState {
  visible: string[];
  known: string[];
}

function loadSaved(storageKey: string): SavedState | null {
  try {
    const raw = localStorage.getItem(`col_picker_${storageKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      // Legacy format — bare visible array. Treat as known too, so
      // nothing the user had hidden gets resurrected.
      return { visible: parsed, known: parsed };
    }
    if (parsed && Array.isArray(parsed.visible) && Array.isArray(parsed.known)) {
      return parsed as SavedState;
    }
    return null;
  } catch {
    return null;
  }
}

function save(storageKey: string, visibleKeys: string[], knownKeys: string[]) {
  const state: SavedState = { visible: visibleKeys, known: knownKeys };
  localStorage.setItem(`col_picker_${storageKey}`, JSON.stringify(state));
}

/** Build the initial visible-column set.
 *
 * - First load (no saved state): all default-visible columns.
 * - Subsequent loads: saved.visible is the source of truth for columns
 *   the user has already seen (saved.known). New columns the user has
 *   NEVER seen (in current columns but not in saved.known) merge in if
 *   they're default-visible — so additions to ALL_COLUMNS aren't silently
 *   invisible. A column the user explicitly hid (in saved.known but not
 *   in saved.visible) STAYS HIDDEN, which is the bug we just fixed. */
function buildInitialVisible(storageKey: string, columns: ColumnDef[]): Set<string> {
  const saved = loadSaved(storageKey);
  const defaultsVisible = columns.filter((c) => c.defaultVisible !== false).map((c) => c.key);
  if (!saved) return new Set(defaultsVisible);

  const known = new Set(saved.known);
  const merged = new Set<string>(saved.visible);
  for (const k of defaultsVisible) {
    // Truly new: the user has never seen this column. Show it.
    if (!known.has(k)) merged.add(k);
  }
  return merged;
}

export function useColumnVisibility(storageKey: string, columns: ColumnDef[]): Set<string> {
  const [visible, setVisible] = useState<Set<string>>(() => buildInitialVisible(storageKey, columns));

  const update = (keys: Set<string>) => {
    setVisible(keys);
    save(storageKey, Array.from(keys), columns.map((c) => c.key));
  };

  return visible;
}

export function ColumnPicker({ storageKey, columns, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState<Set<string>>(() => buildInitialVisible(storageKey, columns));
  const ref = useRef<HTMLDivElement>(null);
  // Snapshot of every column the picker knows about right now. Saved
  // alongside `visible` so a refresh can distinguish "user hid this"
  // from "this column was added later." Re-derived on every render
  // (cheap) so adding a new column to ALL_COLUMNS updates known on the
  // next save without any version bump.
  const knownKeys = columns.map((c) => c.key);

  // Notify parent on mount and changes
  useEffect(() => {
    onChange(visible);
  }, [visible]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggle = (key: string) => {
    const next = new Set(visible);
    if (next.has(key)) {
      if (next.size <= 2) return; // keep at least 2 columns
      next.delete(key);
    } else {
      next.add(key);
    }
    setVisible(next);
    save(storageKey, Array.from(next), knownKeys);
  };

  const reset = () => {
    const defaults = new Set(columns.filter((c) => c.defaultVisible !== false).map((c) => c.key));
    setVisible(defaults);
    save(storageKey, Array.from(defaults), knownKeys);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-medium transition-colors",
          open
            ? "border-brand-navy-300 dark:border-brand-navy-500/40 bg-brand-navy-50 dark:bg-brand-navy-500/10 text-brand-navy-700 dark:text-brand-navy-300"
            : "border-line bg-surface text-ink-muted hover:bg-raised hover:text-ink-2",
        )}
        title="Choose columns"
      >
        <Settings2 className="h-3 w-3" />
        Columns
      </button>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-48 rounded-lg border border-line bg-surface py-1 shadow-lg">
          <div className="px-3 py-1.5 text-[9px] font-semibold uppercase tracking-wider text-ink-subtle">
            Show / Hide Columns
          </div>
          {columns.map((col) => (
            <label
              key={col.key}
              className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs text-ink-2 hover:bg-raised"
            >
              <input
                type="checkbox"
                checked={visible.has(col.key)}
                onChange={() => toggle(col.key)}
                className="h-3 w-3 rounded border-line-strong text-brand-navy-600 dark:text-brand-navy-400 focus:ring-brand-navy-500"
              />
              {col.label}
            </label>
          ))}
          <div className="border-t border-line-soft px-3 py-1.5">
            <button
              onClick={reset}
              className="text-[10px] text-ink-subtle hover:text-ink-3 transition-colors"
            >
              Reset to defaults
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
